import {groupBy, mapValues, uniq} from 'lodash';

import {LogActionsEnum} from '../../page/main/reviews/review-logs/review-log-type';

import {
    isReviewGeneralLogData,
    isReviewTagLogData,
    ReviewLogDataFromApiType,
    ReviewLogTimelineEntryType,
    ReviewTagLogDataType,
    ReviewTagLogGroupType,
    toReviewLogTimelineLog,
} from './reviews-logs';

type TagLogItemType = {
    groupKey: string;
    log: ReviewTagLogDataType;
    title: string;
};

function getReviewLogLoggedAtSecondKey(loggedAt: string): string {
    const timestamp = Date.parse(loggedAt);

    if (Number.isNaN(timestamp)) {
        return loggedAt;
    }

    return new Date(Math.floor(timestamp / 1000) * 1000).toISOString();
}

function getTagLogTitle(log: ReviewTagLogDataType): string | null {
    const title = log.action === LogActionsEnum.Created ? log.newValue : log.oldValue;
    const trimmed = title?.trim() ?? '';

    return trimmed.length > 0 ? trimmed : null;
}

function getTagLogGroupKey(log: ReviewTagLogDataType): string {
    const userKey = log.user ? String(log.user.id) : 'system';

    return `${getReviewLogLoggedAtSecondKey(log.loggedAt)}:${log.action}:${userKey}`;
}

function getTagLogItems(tagLogs: Array<ReviewTagLogDataType>): Array<TagLogItemType> {
    return tagLogs.flatMap((log) => {
        const title = getTagLogTitle(log);

        if (!title) {
            return [];
        }

        return [{groupKey: getTagLogGroupKey(log), log, title}];
    });
}

function getFirstTagLogItem(items: Array<TagLogItemType>): TagLogItemType {
    const firstItem = items[0];

    if (!firstItem) {
        throw new Error('Tag log group must not be empty');
    }

    return firstItem;
}

function buildTagGroupsByKey(tagLogs: Array<ReviewTagLogDataType>): Record<string, ReviewTagLogGroupType> {
    return mapValues(groupBy(getTagLogItems(tagLogs), 'groupKey'), (items): ReviewTagLogGroupType => {
        const firstItem = getFirstTagLogItem(items);

        return {
            action: firstItem.log.action,
            loggedAt: getReviewLogLoggedAtSecondKey(firstItem.log.loggedAt),
            source: 'log-tag-group',
            tagTitles: uniq(items.map((item) => item.title)),
            user: firstItem.log.user,
        };
    });
}

function buildFirstTagGroupIndexByKey(logs: Array<ReviewLogDataFromApiType>): Record<string, number> {
    const tagIndexes = logs.flatMap((log, index) => {
        if (!isReviewTagLogData(log) || !getTagLogTitle(log)) {
            return [];
        }

        return [{groupKey: getTagLogGroupKey(log), index}];
    });

    return mapValues(groupBy(tagIndexes, 'groupKey'), (items) => items[0]?.index ?? -1);
}

export function mapReviewLogsToTimelineEntries(
    logs: Array<ReviewLogDataFromApiType>
): Array<ReviewLogTimelineEntryType> {
    const tagGroupsByKey = buildTagGroupsByKey(logs.filter(isReviewTagLogData));
    const firstTagGroupIndexByKey = buildFirstTagGroupIndexByKey(logs);

    return logs.flatMap((log, index): Array<ReviewLogTimelineEntryType> => {
        if (isReviewGeneralLogData(log)) {
            return [toReviewLogTimelineLog(log)];
        }

        if (!isReviewTagLogData(log)) {
            return [];
        }

        const groupKey = getTagLogGroupKey(log);

        if (!getTagLogTitle(log) || firstTagGroupIndexByKey[groupKey] !== index) {
            return [];
        }

        const group = tagGroupsByKey[groupKey];

        return group ? [group] : [];
    });
}
